import * as THREE from "three";
import { getRandomUnitVector } from "./utils.js";
/**
 * This represents a line using (dual) Plücker coordinates.
 * See: https://en.wikipedia.org/wiki/Pl%C3%BCcker_matrix
 */
export default class Line {
    #xy;
    #xz;
    #xw;
    #yz;
    #yw;
    #zw;
    constructor(v) {
        this.#xy = v[0];
        this.#xz = v[1];
        this.#xw = v[2];
        this.#yz = v[3];
        this.#yw = v[4];
        this.#zw = v[5];
    }
    get normSq() {
        const d = this.#xy * this.#xy +
            this.#xz * this.#xz +
            this.#xw * this.#xw +
            this.#yz * this.#yz +
            this.#yw * this.#yw +
            this.#zw * this.#zw;
        Object.defineProperty(this, "normSq", {
            value: d,
            writable: false,
        });
        return d;
    }
    get norm() {
        // TODO: It might be better to use largest eigen value
        //       of the Plücker matrix instead. It should not
        //       be too hard to compute from the coefficients.
        const d = Math.sqrt(this.normSq);
        Object.defineProperty(this, "norm", {
            value: d,
            writable: false,
        });
        return d;
    }
    get dominant() {
        const dx = this.#zw * this.#zw + this.#yw * this.#yw + this.#yz * this.#yz;
        const dy = this.#zw * this.#zw + this.#xw * this.#xw + this.#xz * this.#xz;
        const dz = this.#yw * this.#yw + this.#xw * this.#xw + this.#xy * this.#xy;
        const dw = this.#yz * this.#yz + this.#xz * this.#xz + this.#xy * this.#xy;
        let i = 3;
        if (dx > dy && dx > dz && dx > dw) {
            i = 0;
        }
        if (dy > dx && dy > dz && dy > dw) {
            i = 1;
        }
        if (dz > dx && dz > dy && dz > dw) {
            i = 2;
        }
        Object.defineProperty(this, "dominant", {
            value: i,
            writable: false,
        });
        return i;
    }
    normalize() {
        const d = this.norm;
        return new Line([
            this.#xy / d,
            this.#xz / d,
            this.#xw / d,
            this.#yz / d,
            this.#yw / d,
            this.#zw / d,
        ]);
    }
    dot(other) {
        return (this.#xy * other.#xy +
            this.#xz * other.#xz +
            this.#xw * other.#xw +
            this.#yz * other.#yz +
            this.#yw * other.#yw +
            this.#zw * other.#zw);
    }
    dual() {
        return new Line([-this.#zw, this.#yw, -this.#yz, -this.#xw, this.#xz, -this.#xy]);
    }
    isTheSame(other) {
        return 1 - Math.abs(this.dot(other)) / (this.norm * other.norm) < 1e-6;
    }
    product(u, target = new THREE.Vector4()) {
        // This corresponds to the following product
        // of Plücker matrix and vector u:
        //
        // |   0 -zw  yw -yz | | x |
        // |  zw   0 -xw  xz | | y |
        // | -yw  xw   0 -xy | | z |
        // |  yz -xz  xy   0 | | w |
        //
        return target.set(-this.#zw * u.y + this.#yw * u.z - this.#yz * u.w, this.#zw * u.x - this.#xw * u.z + this.#xz * u.w, -this.#yw * u.x + this.#xw * u.y - this.#xy * u.w, this.#yz * u.x - this.#xz * u.y + this.#xy * u.z);
    }
    toArray() {
        return [this.#xy, this.#xz, this.#xw, this.#yz, this.#yw, this.#zw];
    }
    toString() {
        return `Line[${this.#xy}:${this.#xz}:${this.#xw}:${this.#yz}:${this.#yw}:${this.#zw}]`;
    }
    getTraversalPlaneAtInfinity() {
        return new THREE.Vector4().setComponent(this.dominant, 1);
    }
    /**
     * A line determines a direction in oriented projective space,
     * and as such it can be used to help order points along the line.
     *
     * Notice that the ordering is not well defined due to the nature of
     * oriented projective space, basically a line is topologically
     * equivalent to a circle. However, if we know from the context
     * that points are included in the same half-space, then it
     * becomes a well defined ordering on that set of points.
     */
    getPointCompare() {
        return (P, Q) => {
            return -this.dot(Line.fromPoints(P, Q));
        };
    }
    static fromPoints(P, Q) {
        return this.fromPlanes(P, Q).dual();
    }
    static fromPlanes(p, q) {
        return new Line([
            p.x * q.y - p.y * q.x,
            p.x * q.z - p.z * q.x,
            p.x * q.w - p.w * q.x,
            p.y * q.z - p.z * q.y,
            p.y * q.w - p.w * q.y,
            p.z * q.w - p.w * q.z,
        ]);
    }
}
export function getRandomDisjointVector(line, epsilon = 1e-6) {
    const n = line.norm;
    if (n < epsilon) {
        throw new Error("Line is degenerate");
    }
    let v = new THREE.Vector4();
    let u = new THREE.Vector4();
    while (u.length() < n * epsilon) {
        v = getRandomUnitVector();
        line.product(v, u);
    }
    return v;
}
export function getPlaneEquation(P1, P2, P3) {
    return Line.fromPlanes(P1, P2).product(P3).negate();
}
//# sourceMappingURL=Line.js.map